"use client";

import * as React from "react";
import { ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

export interface FAQProps {
  items: FAQItem[];
  title?: string;
  showViewAll?: boolean;
  viewAllText?: string;
  onViewAllClick?: () => void;
  className?: string;
  itemClassName?: string;
  questionClassName?: string;
  answerClassName?: string;
}

const FAQ = React.forwardRef<HTMLDivElement, FAQProps>(
  (
    {
      items,
      title = "FAQ",
      showViewAll = false,
      viewAllText = "View all FAQs",
      onViewAllClick,
      className,
      itemClassName,
      questionClassName,
      answerClassName,
      ...props
    },
    ref
  ) => {
    const [openItems, setOpenItems] = React.useState<Set<string>>(new Set());

    const toggleItem = (id: string) => {
      setOpenItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    };

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-black">{title}</h3>
          {showViewAll && (
            <button
              onClick={onViewAllClick}
              className="text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium"
            >
              {viewAllText}
            </button>
          )}
        </div>

        {/* FAQ Items */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {items.map((item, index) => {
            const isOpen = openItems.has(item.id);
            
            return (
              <div
                key={item.id}
                className={cn(
                  "border-b border-gray-200 last:border-b-0",
                  itemClassName
                )}
              >
                {/* Question */}
                <button
                  onClick={() => toggleItem(item.id)}
                  className={cn(
                    "w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 transition-colors border-b border-gray-200",
                    questionClassName
                  )}
                >
                  <span className="flex items-center gap-3">
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-gray-500 transition-transform duration-500",
                        isOpen && "rotate-90"
                      )}
                    />
                    <span className="text-black font-medium text-sm">{item.question}</span>
                  </span>
                </button>

                {/* Answer */}
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-500 ease-in-out",
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  )}
                >
                  <div className="px-4 pt-2 pb-4 text-black leading-tight">
                    {typeof item.answer === 'string' ? (
                      <span className="text-sm">{item.answer}</span>
                    ) : (
                      <div className="text-sm">{item.answer}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

FAQ.displayName = "FAQ";

export { FAQ };
